/**
 * VOCAB de resources — el catálogo de "qué existe", como constantes tipadas.
 *
 * Snapshot **INTERINO**: hoy hand-maintained acá, pero es UNA lista (el vocab),
 * no strings de ruta dispersos por 262 sitios. Futuro: reemplazado por el
 * snapshot del **capability registry** (trudapp/trd-platform#15), consumido por
 * identity, el BFF y este front por igual (build-time, no S2S). Este archivo es
 * el seam de extracción a `@trud/auth-core` (#49).
 *
 * REGLA: los gates (sidebar, rutas, botones) referencian estas constantes vía
 * `can(user, RESOURCES.X, action)`. Nunca un string de ruta ni un id mágico.
 * Los ids son los mismos que sirve el entitlement (`/me.modules`,
 * `/members.resources`) y que enforcea el borde — mismo vocabulario punta a punta.
 */
export const RESOURCES = {
  // Catálogo
  CATALOG_PRODUCTS: "catalog/products",
  CATALOG_CATEGORIES: "catalog/categories",
  // Inventario
  INVENTORY_INVENTORIES: "inventory/inventories",
  INVENTORY_WAREHOUSES: "inventory/warehouses",
  INVENTORY_TRANSFER: "inventory/transfer",
  INVENTORY_LOOSE_PRODUCTS: "inventory/loose-products",
  INVENTORY_PIVOT: "inventory/pivot",
  // Precios
  PRICING_PRICE_LIST: "pricing/price-list",
  PRICING_PRICE_PLAN: "pricing/price-plan",
  PRICING_PRICE_TEMPLATE: "pricing/price-template",
  PRICING_PRICE_WHOLESALER_PLAN: "pricing/price-wholesaler-plan",
  PRICING_PRICE_BULK: "pricing/price-bulk",
  PRICING_MODIFY_PRICE: "pricing/modify-price",
  PRICING_CHECKOUT_FEES: "pricing/checkout-fees",
  // Ventas / órdenes
  ORDER_ORDERS: "order/orders",
  ORDER_ADVANCED: "order/advanced",
  ORDER_SIMPLE: "order/simple",
  // Compras
  PURCHASE_PURCHASES: "purchase/purchases",
  PURCHASE_STOCK_ORDER: "purchase/stock-order",
  PURCHASE_INITIAL_DEBT: "purchase/initial-debt",
  // Cobranzas
  COLLECTION_COLLECTIONS: "collection/collections",
  COLLECTION_DEPOSITS: "collection/deposits",
  COLLECTION_INITIAL_DEBT: "collection/initial-debt",
  // Tesorería
  TREASURY_CASHDESKS: "treasury/cashdesks",
  TREASURY_CHECKS: "treasury/checks",
  TREASURY_EXPENSES: "treasury/expenses",
  // Pagos a proveedores
  DISBURSEMENT_DISBURSEMENTS: "disbursement/disbursements",
  DISBURSEMENT_PAYABLES: "disbursement/payables",
  // Proveedores
  SUPPLIER_SUPPLIERS: "supplier/suppliers",
  SUPPLIER_BRANDS: "supplier/brands",
  // Personas
  PEOPLE_BUYERS: "people/buyers",
  PEOPLE_PAYROLL: "people/payroll",
  // Usuarios
  AUTH_USERS: "auth/users",
  // Reservas
  RESERVATION_RESERVATIONS: "reservation/reservations",
  RESERVATION_BOOKINGS: "reservation/bookings",
  RESERVATION_RESERVABLES: "reservation/reservables",
  RESERVATION_SERVICES: "reservation/services",
  // Cupones
  COUPON_COUPONS: "coupon/coupons",
  // Clínica — vocab REFINADO (platform#20 → identity#56): `clinical` plano y
  // `veterinary/*` quedaron deprecados. El gating usa estos ids refinados; el
  // label (Clínica vs Veterinaria) lo decide `organization.humanHealth`, no el id.
  CLINICAL_PATIENTS: "clinical/patients",
  CLINICAL_VISITS: "clinical/visits",
  CLINICAL_PRACTITIONERS: "clinical/practitioners",
  /** @deprecated `clinical` plano — sólo lo usa el clinical wall (`isClinicalModule`). */
  CLINICAL: "clinical",
  // IA
  AI_CHAT: "ai/chat",
  // Big supplier (B2B)
  BIG_SUPPLIER_ANALYTICS: "big-supplier/analytics",
  BIG_SUPPLIER_BUYERS: "big-supplier/buyers",
  BIG_SUPPLIER_CATALOG: "big-supplier/catalog",
  BIG_SUPPLIER_ORDERS: "big-supplier/orders",
  BIG_SUPPLIER_PRICE_UPDATES: "big-supplier/price-updates",
  // Reportes
  REPORT_REPORTS: "report/reports",
  REPORT_MONITOR: "report/monitor",
  // Tableros
  BOARDS: "boards",
  // Ofertas
  OFFER: "offer",
  // Ventas B2B (seller)
  BIG_SUPPLIER_SELLER: "big-supplier/seller",
} as const;

export type ResourceId = (typeof RESOURCES)[keyof typeof RESOURCES];

/**
 * Los recursos GRANTEABLES del módulo clínico (vocab refinado). El sidebar/grilla
 * clínica gatea sobre estos — NO sobre `clinical` plano ni `veterinary/*` (#59).
 * Un ítem que no mapea a un sub-recurso puntual (ej. "Resumen", "Calendario") se
 * muestra si el usuario puede sobre CUALQUIERA de estos (semántica de módulo).
 */
export const CLINICAL_RESOURCES: ResourceId[] = [
  RESOURCES.CLINICAL_PATIENTS,
  RESOURCES.CLINICAL_VISITS,
  RESOURCES.CLINICAL_PRACTITIONERS,
];
